import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  ThemeProvider,
  UIManager,
  Accordion,
  Box,
  Button,
  Collapse,
  Columns,
  Container,
  Divider,
  Text,
  Media,
  Toaster,
  Toasts,
  MediaQuery,
  IconManager,
  ecommerceLogoPack,
  Link,
} from '@deseretbook/react-ui'
import Turbolinks from 'turbolinks'
import CheckoutNavbar from './CheckoutNavbar'
import CheckoutContext from './CheckoutContext'
import AddressStep from './AddressStep'
import AddressStepSummary from './AddressStepSummary'
import DeliveryStep from './DeliveryStep'
import DeliveryStepSummary from './DeliveryStepSummary'
import PaymentStep from './payment/PaymentStep'
import PaymentStepSummary from './payment/PaymentStepSummary'
import StepNumber from './StepNumber'
import OrderSummary from './OrderSummary'
import MiniOrderSummary from './MiniOrderSummary'
import { DeseretBook } from '../../dbcom/api'
import { Analytics, Logger, formatErrorMessage } from '../../dbcom'
import CheckoutSidebarContent from './CheckoutSidebarContent'
import CheckoutAddons from './CheckoutAddons'

const logger = new Logger({ context: 'Checkout' })

UIManager.injectGlobal()

IconManager.register(ecommerceLogoPack)

/**
 * An edit label that should be displayed in the header of each accordion section if the section
 * is currently clickable.
 *
 * @returns {function} Component
 */
const EditLabel = () => (
  <Box isInline paddingLeft={1}>
    <Text>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link asComponent="span">Edit</Link>
    </Text>
  </Box>
)

/**
 * Checkout page. Where users can go through the checkout process to complete their orders. This
 * manages the page state and the user's flow through each step.
 *
 * @returns {function} Component
 */
class CheckoutPage extends Component {
  /**
   * Page constructor
   * @param {object} props - Page props
   */
  constructor(props) {
    super(props)

    // If users access this page thanks to the browser's back button, the browser
    // loads the page via the cache, and since we don't fetch data on render rather
    // on initial page load we can get situations where the data isn't all that
    // we want. We should probably change this to fetch the order on page load,
    // until then though we'll check to see how the user got to the page and if
    // it was via the back button then we'll just refresh the page.
    if (
      // If the performance API is available
      window.performance
      // If the page was loaded thanks to the back button
      && window.performance.navigation.type === 2
    ) {
      window.location.reload(true) // force hard reload
    }

    this.state = {
      step: props.order.state,
      order: null,
      user: props.user,
      isLoading: false,
      services: {
        avatax: true,
        bookshelf: true,
        braintree: true,
        intelligentSearch: true,
        relate: true,
      },
    }

    this.initializeData = this.initializeData.bind(this)
    this.checkServiceHealth = this.checkServiceHealth.bind(this)
    this.trackCheckoutStep = this.trackCheckoutStep.bind(this)
    this.trackRevenue = this.trackRevenue.bind(this)
    this.updateUser = this.updateUser.bind(this)
    this.updateOrder = this.updateOrder.bind(this)
    this.startLoading = this.startLoading.bind(this)
    this.stopLoading = this.stopLoading.bind(this)

    this.stepIsOpen = this.stepIsOpen.bind(this)
    this.stepIsDisabled = this.stepIsDisabled.bind(this)
    this.changeStep = this.changeStep.bind(this)
    this.submitOrder = this.submitOrder.bind(this)
  }

  /**
   * When the page is first loaded we want to fetch useful data that we need to display stuff.
   */
  componentDidMount() {
    this.initializeData()
    this.checkServiceHealth()
  }

  /**
   * Retrieves critical data from API and sets up the checkout page.
   */
  async initializeData() {
    const { order } = this.props

    try {
      const orderResponse = await DeseretBook.order(order).get()

      this.setState({ order: orderResponse }, () => {
        Analytics.event({ category: 'Ecommerce', action: 'SinglePageCheckout', label: 'SinglePageCheckout' }, { isInteractive: false })
        this.trackCheckoutStep()
      })
    } catch ({ message, errors }) {
      Toasts.danger('Unable to find order', formatErrorMessage(message, errors))
      logger.critical(`Unable to initialize checkout page: "${formatErrorMessage(message, errors)}"`, {
        errors,
        orderProp: order,
      })
    }
  }

  /**
   * Checks to see what the health status of our 3rd-party API services are. We use this to see what
   * features to allow users access to.
   */
  async checkServiceHealth() {
    try {
      const health = await DeseretBook.health()

      this.setState(() => ({
        services: health,
      }))
    } catch ({ message, errors }) {
      // We can just fail silently, we'll assume all is well.
      // <InsertMemeHere src="https://knowyourmeme.com/memes/this-is-fine" />
      logger.warning(`Unable to get service health: "${formatErrorMessage(message, errors)}"`, { errors })
    }
  }

  /**
   * Handles tracking revenue
   * Reports to: Google Analytics.
   *
   * @param {function} [callback] - Callback after order placed.
   */
  trackRevenue(callback) {
    const { order } = this.state

    try {
      Analytics
        .ecommerce()
        .purchase({
          actionField: {
            id: order.number,
            affiliation: 'DeseretBook.com',
            revenue: order.total,
            tax: order.taxTotal,
            shipping: order.shipTotal,
            coupon: order.couponCode,
          },
          products: order.lineItems
            .map(({ variant, price, quantity }) => ({
              quantity,
              price,
              name: variant.name,
              id: variant.sku,
              brand: variant.analyticsBrand,
              category: variant.analyticsCategory,
              variant: variant.optionsTextAnalytics,
            })),
        }, { callback })
    } catch ({ message }) {
      logger.warning(`Unable to track e-commerce revenue: "${message}"`)
    }
  }

  /**
   * Handles event tracking of what the current checkout step is.
   * Reports to: Bronto, Google Analytics.
   *
   * @param {function} [callback] - Callback after step tracked.
   */
  trackCheckoutStep(callback) {
    const { step, order } = this.state


    // Track with Google Analytics
    let gaStepNumber = 4
    let brontoCartPhase = 'ORDER_REVIEW'

    if (step === 'address') {
      gaStepNumber = 1
      brontoCartPhase = 'SHIPPING_INFO'
    } else if (step === 'delivery') {
      gaStepNumber = 2
      brontoCartPhase = 'SHIPPING_METHOD'
    } else if (step === 'payment') {
      gaStepNumber = 3
      brontoCartPhase = 'PAYMENT'
    } else if (step === 'complete') {
      gaStepNumber = 5
      brontoCartPhase = 'ORDER_COMPLETE'
    }

    try {
      Analytics
        .ecommerce()
        .checkoutStep({
          products: order.lineItems
            .map(({ variant, price, quantity }) => ({
              quantity,
              price,
              name: variant.name,
              id: variant.sku,
              brand: variant.analyticsBrand,
              category: variant.analyticsCategory,
              variant: variant.optionsTextAnalytics,
            })),
          step: gaStepNumber,
        }, { callback })
    } catch ({ message }) {
      logger.warning(`Unable to track e-commerce step: "${message}"`)
    }

    // Now we can track Bronto
    try {
      if (typeof window.bronto === 'function') {
        window.bronto('cart:send', {
          phase: brontoCartPhase,
          currency: 'USD',
          subtotal: order.itemTotal,
          discountedAmount: order.discountedAmount,
          shippingAmount: order.shipmentTotal,
          taxAmount: order.taxTotal,
          grandTotal: order.total,
          customerOrderId: order.number,
          orderId: order.number,
          emailAddress: order.email,
          cartUrl: `${window.location.origin}/cart`,
          lineItems: order.lineItems
            .map(({
              variant, price, quantity, total,
            }) => ({
              quantity,
              price,
              sku: variant.sku,
              name: variant.name,
              description: variant.analyticsDescription,
              category: variant.analyticsCategory,
              unitPrice: variant.originalPrice,
              totalPrice: total,
              imageUrl: variant.images[0] && variant.images[0].largeUrl,
              productUrl: `${window.location.origin}/p/${variant.slug}`,
            })),
        })
      }
    } catch ({ message }) {
      logger.warning(`Unable to send step to Bronto: "${message}"`)
    }
  }

  /**
   * Updates the page's instance of the user's data.
   *
   * @param {object} [updates={}] - Changes to the user stored in state.
   */
  updateUser(updates = {}) {
    this.setState(({ user }) => ({
      isLoading: false,
      user: {
        ...user,
        ...updates,
      },
    }))
  }

  /**
   * Updates the page's instance of the order's data.
   *
   * @param {object} [updates={}] - Changes to the order stored in state.
   * @param {boolean} [advanceStep=false] - If the page should attempt to advance to the next step.
   */
  updateOrder(updates = {}, advanceStep = false) {
    this.setState(({ order }) => {
      const updatedState = {
        isLoading: false,
        order: {
          ...order,
          ...updates,
        },
      }

      if (advanceStep) {
        updatedState.step = updates.state || order.state
      }

      return updatedState
    }, this.trackCheckoutStep)
  }

  /**
   * Indicates to the page that something is loading, so all functions should be suspended.
   *
   * @param {function} [callback] - Optional callback to run once the loading state is set.
   */
  startLoading(callback) {
    this.setState({ isLoading: true }, callback)
  }

  /**
   * Lets the page know that loading is stopped, so all functions can be resumed.
   *
   * @param {function} [callback] - Optional callback to run once the loading state is set.
   */
  stopLoading(callback) {
    this.setState({ isLoading: false }, callback)
  }

  /**
   * Helper that returns true of the given step is the one that's open.
   *
   * @param {string} step - Step name
   * @returns {boolean} If the given step is the one that's open.
   */
  stepIsOpen(step) {
    return this.state.step === step
  }

  /**
   * Helper that returns true if the given step is disabled, or if the user can interact with it.
   * @param {string} step - Step name
   * @returns {boolean} If the given step is disabled
   */
  stepIsDisabled(step) {
    const { isLoading } = this.state
    const { state } = this.state.order
    let enabledSteps = ['address', 'delivery', 'payment']

    if (state === 'address') {
      enabledSteps = ['address']
    } else if (state === 'delivery') {
      enabledSteps = ['address', 'delivery']
    }

    return isLoading || enabledSteps.indexOf(step) === -1
  }

  /**
   * Changes the active step to the one given. If one isn't given, or if the step can't be changed,
   * then nothing happens.
   * @param {string} nextStep - Name of next step to activate
   */
  changeStep(nextStep) {
    const { state } = this.state.order

    if (!nextStep || this.stepIsDisabled(nextStep)) {
      return
    }

    this.setState(({ step }) => ({
      step: nextStep === step
        ? state
        : nextStep,
    }), this.trackCheckoutStep)
  }

  /**
   * This tells the server the order is all ready to go and should be processed.
   */
  submitOrder() {
    if (this.state.isLoading || this.state.isSubmitting) {
      return
    }

    this.setState({ isSubmitting: true, isLoading: true }, async () => {
      const { order, user } = this.state

      try {
        const submittedOrder = await DeseretBook
          .checkout(order)
          .complete()

        this.setState({ order: submittedOrder, step: submittedOrder.state }, () => {
          this.trackCheckoutStep(() => {
            this.trackRevenue(() => {
              Turbolinks.visit(user
                ? `/orders/${submittedOrder.number}`
                : `/orders/${submittedOrder.number}/token/${submittedOrder.token}`)
            })
          })
        })
      } catch ({ message, errors }) {
        this.setState({ isSubmitting: false, isLoading: true })
        Toasts.danger('Unable to submit order', formatErrorMessage(message, errors))
        logger.critical(`Unable to submit order: "${formatErrorMessage(message, errors)}"`, {
          errors,
          message,
          order,
          user,
        })
      }
    })
  }

  /**
   * Figures out how the component should be rendered.
   * @returns {function} Component
   */
  render() {
    const {
      order,
      user,
      isLoading,
      isSubmitting,
      services,
    } = this.state
    const { stores, availablePaymentMethods } = this.props

    return (
      <ThemeProvider>
        <CheckoutContext.Provider
          value={{
            user,
            order,
            updateUser: this.updateUser,
            updateOrder: this.updateOrder,
            startLoading: this.startLoading,
            stopLoading: this.stopLoading,
            status: {
              isLoading,
              services,
            },
          }}
        >
          <MediaQuery.Medium>
            {isMediumSized => (
              <Fragment>
                <Toaster />
                <Box isColumn minHeight="100vh">
                  <CheckoutNavbar />

                  <Container style={{ flex: 1, display: 'flex' }}>
                    <Box flex={1} isColumn paddingVertical={0.5} maxWidth="100%">
                      <Columns style={{ flex: 1 }}>
                        <Columns.Column size={{ medium: 8, large: 9 }}>
                          {!!order && (
                            <React.Fragment>
                              <Accordion isUncontrolled>
                                <Accordion.Section
                                  isOpen={this.stepIsOpen('address')}
                                  isDisabled={this.stepIsDisabled('address')}
                                  onToggle={() => this.changeStep('address')}
                                >
                                  <Accordion.Section.Header>
                                    <Media>
                                      <Media.Figure verticalAlignment="center">
                                        <StepNumber
                                          isActive={this.stepIsOpen('address')}
                                          number={1}
                                        />
                                      </Media.Figure>
                                      <Media.Content>
                                        <Text
                                          size="large"
                                          color={!this.stepIsOpen('address') && 'muted'}
                                          display="block"
                                        >
                                          Delivery Address
                                          {(!this.stepIsOpen('address') && !this.stepIsDisabled('address')) && (
                                            <EditLabel />
                                          )}
                                        </Text>
                                      </Media.Content>
                                    </Media>
                                  </Accordion.Section.Header>
                                  <Accordion.Section.StaticContent>
                                    {({ isOpen }) => (
                                      <Collapse isCollapsed={isOpen}>
                                        <Box paddingLeft={isMediumSized && 2.5}>
                                          <AddressStepSummary />
                                        </Box>
                                      </Collapse>
                                    )}
                                  </Accordion.Section.StaticContent>
                                  <Accordion.Section.Content>
                                    <Box paddingLeft={isMediumSized && 2.5}>
                                      <AddressStep
                                        isActive={this.stepIsOpen('address')}
                                        onCloseStep={() => this.changeStep('address')}
                                      />
                                    </Box>
                                  </Accordion.Section.Content>
                                </Accordion.Section>

                                <Divider />

                                <Accordion.Section
                                  isOpen={this.stepIsOpen('delivery')}
                                  isDisabled={this.stepIsDisabled('delivery')}
                                  onToggle={() => this.changeStep('delivery')}
                                >
                                  <Accordion.Section.Header>
                                    <Media>
                                      <Media.Figure verticalAlignment="center">
                                        <StepNumber
                                          isActive={this.stepIsOpen('delivery')}
                                          isDisabled={this.stepIsDisabled('delivery')}
                                          number={2}
                                        />
                                      </Media.Figure>
                                      <Media.Content>
                                        <Text
                                          size="large"
                                          color={
                                            (!this.stepIsOpen('delivery')
                                            && !this.stepIsDisabled('delivery')
                                            && 'muted')
                                            || (this.stepIsDisabled('delivery') && 'disabled')
                                          }
                                          display="block"
                                        >
                                          Delivery Method
                                          {(!this.stepIsOpen('delivery') && !this.stepIsDisabled('delivery')) && (
                                            <EditLabel />
                                          )}
                                        </Text>
                                      </Media.Content>
                                    </Media>
                                  </Accordion.Section.Header>
                                  <Accordion.Section.StaticContent>
                                    {({ isOpen }) => (
                                      <Collapse isCollapsed={isOpen}>
                                        <Box paddingLeft={isMediumSized && 2.5}>
                                          <DeliveryStepSummary />
                                        </Box>
                                      </Collapse>
                                    )}
                                  </Accordion.Section.StaticContent>
                                  <Accordion.Section.Content>
                                    <Box paddingLeft={isMediumSized && 2.5}>
                                      <DeliveryStep
                                        stores={stores}
                                        isActive={this.stepIsOpen('delivery')}
                                        onCloseStep={() => this.changeStep('delivery')}
                                      />
                                    </Box>
                                  </Accordion.Section.Content>
                                </Accordion.Section>

                                <Divider />

                                <Accordion.Section
                                  isOpen={this.stepIsOpen('payment')}
                                  isDisabled={this.stepIsDisabled('payment')}
                                  onToggle={() => this.changeStep('payment')}
                                >
                                  <Accordion.Section.Header>
                                    <Media>
                                      <Media.Figure verticalAlignment="center">
                                        <StepNumber
                                          isActive={this.stepIsOpen('payment')}
                                          isDisabled={this.stepIsDisabled('payment')}
                                          number={3}
                                        />
                                      </Media.Figure>
                                      <Media.Content>
                                        <Text
                                          size="large"
                                          display="block"
                                          color={
                                            (!this.stepIsOpen('payment')
                                            && !this.stepIsDisabled('payment')
                                            && 'muted') || (
                                              this.stepIsDisabled('payment') && 'disabled'
                                            )
                                          }
                                        >
                                          Payment Method
                                          {(!this.stepIsOpen('payment') && !this.stepIsDisabled('payment')) && (
                                            <EditLabel />
                                          )}
                                        </Text>
                                      </Media.Content>
                                    </Media>
                                  </Accordion.Section.Header>
                                  <Accordion.Section.StaticContent>
                                    {({ isOpen }) => (
                                      <Collapse isCollapsed={isOpen}>
                                        <Box paddingLeft={isMediumSized && 2.5}>
                                          <PaymentStepSummary
                                            availablePaymentMethods={availablePaymentMethods}
                                          />
                                        </Box>
                                      </Collapse>
                                    )}
                                  </Accordion.Section.StaticContent>
                                  <Accordion.Section.Content>
                                    <Box paddingLeft={isMediumSized && 2.5}>
                                      <PaymentStep
                                        availablePaymentMethods={availablePaymentMethods}
                                        isActive={this.stepIsOpen('payment')}
                                        onCloseStep={() => this.changeStep('payment')}
                                      />
                                    </Box>
                                  </Accordion.Section.Content>
                                </Accordion.Section>
                              </Accordion>
                              {!isMediumSized && (
                                <Fragment>
                                  <Box paddingVertical={1}>
                                    <CheckoutAddons />
                                  </Box>
                                  <CheckoutSidebarContent />
                                  <Box paddingTop={4} paddingBottom={1} justifyContent="center">
                                    <Button
                                      type="button"
                                      color="light"
                                      onClick={() => {
                                        if (!window.CustomerSupport) {
                                          return
                                        }

                                        window.CustomerSupport.openChat()
                                        window.CustomerSupport.hideChatBubble()
                                      }}
                                    >
                                      Have Questions? Contact Us
                                    </Button>
                                  </Box>
                                </Fragment>
                              )}
                            </React.Fragment>
                          )}
                        </Columns.Column>
                        {!!isMediumSized && (
                          <Columns.Column background="alternate">
                            {!!(order) && (
                              <OrderSummary
                                submitOrder={this.submitOrder}
                                isLoading={isLoading}
                                isSubmitting={isSubmitting}
                              />
                            )}
                          </Columns.Column>
                        )}
                      </Columns>
                    </Box>
                  </Container>
                  {!!(!isMediumSized && order) && (
                    <MiniOrderSummary
                      submitOrder={this.submitOrder}
                      isLoading={isLoading}
                      isSubmitting={isSubmitting}
                    />
                  )}
                </Box>
              </Fragment>
            )}
          </MediaQuery.Medium>
        </CheckoutContext.Provider>
      </ThemeProvider>
    )
  }
}

CheckoutPage.propTypes = {
  order: PropTypes.shape({
    state: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({}),
  stores: PropTypes.arrayOf(PropTypes.shape({})),
  availablePaymentMethods: PropTypes.arrayOf(PropTypes.shape({})),
}

CheckoutPage.defaultProps = {
  user: undefined,
  stores: [],
  availablePaymentMethods: [],
}

export default CheckoutPage
